import React from 'react';

import { Box, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Chip from 'domains/core/components/Chip';
import {
    EventSelector,
    SegmentDefinition,
    SegmentDemographic,
    SegmentEngagement,
    SegmentEvent,
    EngagementAPIOperator,
    EngagementName,
    EngagementUIOperator,
} from 'domains/segments/types';
import useSendById from 'hooks/queries/useSendById';

import FilterDetails from '../FilterDetails';
import { secondarySpanProps } from '../FilterDetails/FilterDetails';

const INDENT_SPACING = 4;

type ConnectorType = 'where' | 'and' | 'or';

type SegmentItem =
    | { type: 'contactRecordType'; recordTypes: string[] }
    | { type: 'demographic'; filter: SegmentDemographic }
    | { type: 'engagement'; filter: SegmentEngagement }
    | { type: 'contactEvent'; filter: SegmentEvent }
    | { type: 'contactObject'; filter: SegmentEvent }
    | { type: 'group'; definition: SegmentDefinition };

const toSegmentItems = (definition: SegmentDefinition): SegmentItem[] => [
    ...((definition.contactRecordType?.length ?? 0) > 0
        ? [{ type: 'contactRecordType', recordTypes: definition.contactRecordType as string[] } as SegmentItem]
        : []),
    ...(definition.demographic ?? []).map((filter): SegmentItem => ({ type: 'demographic', filter })),
    ...(definition.engagement ?? []).map((filter): SegmentItem => ({ type: 'engagement', filter })),
    ...(definition.contactEvent ?? []).map((filter): SegmentItem => ({ type: 'contactEvent', filter })),
    ...(definition.contactObject ?? []).map((filter): SegmentItem => ({ type: 'contactObject', filter })),
    ...(definition.subsegments ?? []).map((def): SegmentItem => ({ type: 'group', definition: def })),
];

const ConnectorChip = ({ type }: { type: ConnectorType }) => {
    const baseStyles = {
        mr: 1,
        my: '2px',
        textTransform: 'lowercase' as const,
        minWidth: '48px',
        flexShrink: 0,
    };

    if (type === 'where') {
        return <Chip label={type} sx={{ ...baseStyles, backgroundColor: 'grey.300', color: 'text.primary' }} />;
    }

    if (type === 'or') {
        return <Chip label={type} sx={baseStyles} variant="outlined" />;
    }

    return <Chip label={type} sx={baseStyles} />;
};

const FilterRow = ({
    connector,
    depth,
    children,
}: {
    connector: ConnectorType;
    depth: number;
    children: React.ReactNode;
}) => (
    <Box display="flex" alignItems="flex-start" pl={depth * INDENT_SPACING} mb={0.5}>
        <ConnectorChip type={connector} />
        <Box component="span" display="inline" sx={{ pt: '3px', lineHeight: 1.8 }}>
            {children}
        </Box>
    </Box>
);

const getEventSelectorText = (selector: string, operator?: string): string => {
    if (selector === 'any' && (!operator || operator === 'exists')) return EventSelector.INCLUDES;
    if (selector === 'any' && operator === 'not exists') return EventSelector.DOES_NOT_INCLUDE;
    return EventSelector.INCLUDES;
};

const engagementActionMap: Partial<Record<EngagementName, Partial<Record<EngagementAPIOperator, string>>>> = {
    [EngagementName.RECEIVED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.DID_RECEIVE,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.DID_NOT_RECEIVE,
    },
    [EngagementName.DELIVERED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.DID_DELIVER,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.DID_NOT_DELIVER,
    },
    [EngagementName.CLICKED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.DID_CLICK,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.DID_NOT_CLICK,
    },
    [EngagementName.OPENED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.DID_OPEN,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.DID_NOT_OPEN,
    },
    [EngagementName.REPLIED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.DID_REPLY,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.DID_NOT_REPLY,
    },
    [EngagementName.CALL_ANSWERED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.CALL_ANSWERED,
    },
    [EngagementName.NOT_PICKED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.CALL_NOT_ANSWERED,
    },
    [EngagementName.VOICEMAIL_SENT_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.VOICEMAIL,
    },
    [EngagementName.CALL_FAILED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.CALL_FAILED,
    },
    [EngagementName.CALL_TRANSFERED_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.CALL_TRANSFERRED,
    },
    [EngagementName.SENT_SENDS]: {
        [EngagementAPIOperator.CONTAINS]: EngagementUIOperator.SENT,
        [EngagementAPIOperator.DOES_NOT_CONTAIN]: EngagementUIOperator.NOT_SENT,
    },
};

const getEngagementAction = ({ name, operator }: SegmentEngagement): string =>
    engagementActionMap[name]?.[operator] ?? operator;

const CurationName = ({ sendId }: { sendId: number }) => {
    const { data, isError, isLoading } = useSendById(sendId);
    if (isError) return null;
    if (isLoading) return <>Loading...</>;
    return <>{data?.name}</>;
};

const EngagementDescription = ({ engagement }: { engagement: SegmentEngagement }) => {
    const action = getEngagementAction(engagement);
    const values = engagement.segmentLinkValue ? [engagement.segmentLinkValue] : engagement.additionalValues ?? [];

    return (
        <>
            <Typography {...secondarySpanProps}>Contact Activity </Typography>
            <Typography component="span">{action} </Typography>
            {values.length > 0 && (
                <>
                    <Typography {...secondarySpanProps}>{values.join(', ')} </Typography>
                    <Typography component="span">in </Typography>
                </>
            )}
            <Typography {...secondarySpanProps}>
                <CurationName sendId={engagement.value} />
            </Typography>
        </>
    );
};

type DefinitionBlockProps = {
    definition: SegmentDefinition;
    depth: number;
    isRoot: boolean;
    parentConnector?: ConnectorType;
    parentDepth?: number;
};

const DefinitionBlock = ({ definition, depth, isRoot, parentConnector, parentDepth }: DefinitionBlockProps) => {
    const { contactObject: hasContactObjectFlag } = useFlags();
    const connector = definition.matchType.toLowerCase() as ConnectorType;
    const items = toSegmentItems(definition).filter((item) => hasContactObjectFlag || item.type !== 'contactObject');

    return (
        <>
            {items.map((item, i) => {
                const itemConnector = i === 0 ? (isRoot ? 'where' : parentConnector ?? connector) : connector;
                const itemDepth = i === 0 && parentDepth !== undefined ? parentDepth : depth;

                if (item.type === 'group') {
                    return (
                        <DefinitionBlock
                            key={`g-${depth}-${i}`}
                            definition={item.definition}
                            depth={depth + 1}
                            isRoot={false}
                            parentConnector={itemConnector}
                            parentDepth={itemDepth}
                        />
                    );
                }

                if (item.type === 'contactRecordType') {
                    return (
                        <FilterRow key={`rt-${depth}-${i}`} connector={itemConnector} depth={itemDepth}>
                            <Typography {...secondarySpanProps}>Contact Type </Typography>
                            <Typography component="span">includes </Typography>
                            <Typography {...secondarySpanProps} sx={{ textTransform: 'capitalize' }}>
                                {item.recordTypes.join(', ')}
                            </Typography>
                        </FilterRow>
                    );
                }

                if (item.type === 'demographic') {
                    return (
                        <FilterRow key={`d-${depth}-${i}`} connector={itemConnector} depth={itemDepth}>
                            <Typography {...secondarySpanProps}>Contact Details </Typography>
                            <Typography component="span">includes </Typography>
                            <FilterDetails
                                includeNullValues={item.filter.includeNullValues}
                                name={item.filter.name}
                                operator={item.filter.operator}
                                sqlType={item.filter.sqlType}
                                value={item.filter.value}
                            />
                        </FilterRow>
                    );
                }

                if (item.type === 'engagement') {
                    return (
                        <FilterRow key={`e-${depth}-${i}`} connector={itemConnector} depth={itemDepth}>
                            <EngagementDescription engagement={item.filter} />
                        </FilterRow>
                    );
                }

                if (item.type === 'contactObject') {
                    const recordType = item.filter.conditions.find((c) => c.name === 'record_type');
                    const conditions = item.filter.conditions.filter((c) => c.name !== 'record_type');

                    return (
                        <React.Fragment key={`co-${depth}-${i}`}>
                            <FilterRow connector={itemConnector} depth={itemDepth}>
                                <Typography {...secondarySpanProps}>Contact Object </Typography>
                                <Typography component="span">
                                    {getEventSelectorText(item.filter.selector, item.filter.operator)}{' '}
                                </Typography>
                                {recordType?.value && (
                                    <Typography {...secondarySpanProps}>{String(recordType.value)}</Typography>
                                )}
                            </FilterRow>
                            {conditions.map((condition, ci) => (
                                <FilterRow
                                    key={`coc-${depth}-${ci}`}
                                    connector={ci === 0 ? 'where' : 'and'}
                                    depth={depth + 1}
                                >
                                    <FilterDetails
                                        includeNullValues={condition.includeNullValues}
                                        name={condition.name}
                                        operator={condition.operator}
                                        sqlType={condition.sqlType}
                                        value={condition.value}
                                    />
                                </FilterRow>
                            ))}
                        </React.Fragment>
                    );
                }

                const recordType = item.filter.conditions.find((c) => c.name === 'record_type');
                const conditions = item.filter.conditions.filter((c) => c.name !== 'record_type');

                return (
                    <React.Fragment key={`ce-${depth}-${i}`}>
                        <FilterRow connector={itemConnector} depth={itemDepth}>
                            <Typography {...secondarySpanProps}>Contact Event </Typography>
                            <Typography component="span">
                                {getEventSelectorText(item.filter.selector, item.filter.operator)}{' '}
                            </Typography>
                            {recordType?.value && (
                                <Typography {...secondarySpanProps}>{String(recordType.value)}</Typography>
                            )}
                        </FilterRow>
                        {conditions.map((condition, ci) => (
                            <FilterRow
                                key={`cc-${depth}-${ci}`}
                                connector={ci === 0 ? 'where' : 'and'}
                                depth={depth + 1}
                            >
                                <FilterDetails
                                    includeNullValues={condition.includeNullValues}
                                    name={condition.name}
                                    operator={condition.operator}
                                    sqlType={condition.sqlType}
                                    value={condition.value}
                                />
                            </FilterRow>
                        ))}
                    </React.Fragment>
                );
            })}
        </>
    );
};

type SegmentDetailsDisplayProps = {
    segmentDefinition: SegmentDefinition;
};

const SegmentDetailsDisplay = ({ segmentDefinition }: SegmentDetailsDisplayProps) => (
    <Box>
        <DefinitionBlock definition={segmentDefinition} depth={0} isRoot />
    </Box>
);

export default SegmentDetailsDisplay;
